import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {TopicIndexService} from '@shared/services/topic-index.service';
import {TopicPage} from '@shared/models/topic-page.model';
import {TopicVideo} from '@shared/models/topic-video.model';
import {TopicQuestionary} from '@shared/models/topic-questionary.model';
import {TopicQuestionDetails} from '@shared/models/topic-question.model';
import {TopicIndex} from '@shared/models/topic-index.model';
import {TopicQuestionaryService} from '@shared/services/topic-questionary.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslationService} from '@shared/services/translation.service';

@Component({
    selector: 'saf-schooling-index-content',
    templateUrl: './schooling-index-content.component.html',
    styleUrls: ['./schooling-index-content.component.scss'],
    standalone: false
})
export class SchoolingIndexContentComponent implements OnChanges {
  @Input() public indexId: number = null;
  @Input() public onlineId: number = null;
  @Input() public fire = false;
  @Output() public result = new EventEmitter<any>();
  @Output() public isContentLoading = new EventEmitter<any>();
  public loading: boolean = false;
  public indexContentData: any;
  public source: string;
  public index: TopicIndex;
  public page: TopicPage = null;
  public video: TopicVideo = null;
  public questionary: TopicQuestionary = null;
  public questionDetails: TopicQuestionDetails[] = [];
  public questionResultsMap: { [key: number]: boolean } = {};  resultText: any;
  success: any;
  @ViewChild('image') imageElement!: ElementRef;
  scale = 100;
  lastScale = 100;
  lastTap = 0;
  private overlayTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private indexService: TopicIndexService,
    private topicQuestionaryService: TopicQuestionaryService,
    private translationService: TranslationService,
    private snackBar: MatSnackBar,
  ) { }

  onPinch(event: any) {
    const zoomStep = event.deltaY > 0 ? -5 : 5;
    this.scale = Math.max(50, Math.min(this.lastScale * event.scale, 300)); // Zoom zwischen 1x - 3x
    this.applyTransform();
  }

  onPinchEnd() {
    this.lastScale = this.scale;
  }

  @HostListener('click', ['$event'])
  onDoubleTap(event: MouseEvent | TouchEvent) {
    const currentTime = new Date().getTime();
    if (currentTime - this.lastTap < 300) {
      this.resetImage();
    }
    this.lastTap = currentTime;
  }

  applyTransform() {
    this.imageElement.nativeElement.style.maxWidth = `${this.scale}%`;
  }

  resetImage() {
    this.scale = 100;
    this.applyTransform();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.fire) {
      this.showQuestionResult();
    } else {
      if (changes['indexId'] && changes['indexId'].currentValue !== changes['indexId'].previousValue) {
        this.getContent();
      }
    }
  }

  getContent() {
    this.clearResultOverlay();
    this.loading = true;
    this.isContentLoading.emit(this.loading);
    if (this.indexId) {
      this.indexService.getContent(this.indexId)
        .subscribe((data: any) => {
          this.indexContentData = data;
          this.index = data.index;
          this.typeSwitch(data.index.pageType, data);
          this.loading = false;
          this.isContentLoading.emit(this.loading);
        });
    }
  }

  typeSwitch(type: number, data: any) {
    switch (type) {
      case 0:
        this.page = data.page;
        this.source = data.serverAddress +  data.page.pictureURL;
        break;
      case 1:
        this.questionary = data.questionary;
        this.questionDetails = data.questions;
        break;
      case 2:
        this.video = this.indexContentData.video;
        this.source = data.serverAddress + '/uploads/videos/' + data.video.filename;
        break;
    }
  }

  showQuestionResult() {
    const allCheckboxes: any = document.querySelectorAll('.answerInputField');
    const answers: any = [];
    const questionsArray: any = [];

    for (let i = 0; i < allCheckboxes.length; i++) {
      if (allCheckboxes[i].checked) {
        answers.push(allCheckboxes[i].value);
      }
    }

    document.querySelectorAll('.schoolingContentQuestionContainer').forEach(question => {
        const questionId = question.id;
        questionsArray.push(questionId);
      }
    );

    const params = {
      answers : answers,
      questions : questionsArray
    };

    if (this.onlineId) {
      params['onlineId'] = this.onlineId;
    }

    this.topicQuestionaryService.getQuestionaryResult(params).subscribe(result => {
      this.handleResult(result);
    });
  }

  handleResult(result) {
    let response: any;
    if (result) {
      if (result.failureReason === 'noAnswerGiven') {
        response = [this.translationService.translate('noAnswerGiven'), result];
        this.success = response;
      } else {
        response = [this.translationService.translate('failed'), result];
        this.success = response;
      }
      if (result.success) {
        response = [this.translationService.translate('success'), result];
        this.success = response;
      }

      if (result.questionResults && Array.isArray(result.questionResults)) {
        this.questionResultsMap = {};

        result.questionResults.forEach((res: any) => {
          this.questionResultsMap[res.question.id] = res.correct;
        });
      }
    }

    TopicQuestionaryService.clearCache();
    this.resultText = response[0];
    this.result.emit(response);
    this.hideOverlay();
  }

  hideOverlay() {
    if (this.overlayTimeoutId) {
      clearTimeout(this.overlayTimeoutId);
    }

    this.overlayTimeoutId = setTimeout(() => {
      this.clearResultOverlay();
    }, 3000);
  }

  clearResultOverlay() {
    this.resultText = '';
    if (this.overlayTimeoutId) {
      clearTimeout(this.overlayTimeoutId);
      this.overlayTimeoutId = null;
    }
  }

  setStandardQuestionCheckboxes(questionId: number, answerId: number) {
    const boxes: any = document.querySelectorAll('.answerOfQuestion_' + questionId);
    const check: any = document.querySelector('#answer_' + answerId);
    for (let i = 0; i < boxes.length; i++) {
      boxes[i].checked = false;
    }
    check.checked = true;
  }

  toggleAnswer(questionId: number, answerId: number, questionType: number) {
    const check: any = document.querySelector('#answer_' + answerId);

    if (!check) {
      return;
    }

    if (questionType === 0) {
      this.setStandardQuestionCheckboxes(questionId, answerId);
      return;
    }

    check.checked = !check.checked;
  }
}
